import type { Plan } from '../api/types';
import type { IkonNavn } from '../components/icons';

/**
 * Hjelpesidene: brukerveiledningen og de vanlige spørsmålene.
 *
 * Det som står her, er *strukturen* – hvilke kapitler som finnes, i hvilken
 * rekkefølge, og hvordan man søker i spørsmålene. Selve tekstene ligger i
 * `brukerveiledning.tsx` og `vanligeSporsmal.ts`. Skillet er med vilje: dette
 * er rene funksjoner uten React, og det er dem som er verdt å teste – at hvert
 * verktøy har et kapittel, at ingen lenke peker på et kapittel som ikke finnes,
 * og at «ønskeliste» finner «onskeliste».
 *
 * Kapitlene følger kundereisen, ikke menyen: man leser en veiledning i den
 * rekkefølgen man gjør tingene, og gjestelista kommer før bordkartet uansett
 * hvor de står i sidemenyen.
 */

export type Kapittelgruppe = 'start' | 'verktoy' | 'konto';

export type Kapittel = {
  /** Står i adressen: `/app/hjelp/<slug>`. Skal ikke endres etterpå – lenker er i omløp. */
  slug: string;
  tittel: string;
  ikon: IkonNavn;
  gruppe: Kapittelgruppe;
  /** Pakken verktøyet krever, der det ikke er med i Gratis. */
  krever?: Exclude<Plan, 'free'>;
  /** Én setning under overskriften: hva kapittelet svarer på. */
  ingress: string;
};

export const GRUPPENAVN: Record<Kapittelgruppe, string> = {
  start: 'Kom i gang',
  verktoy: 'Verktøyene',
  konto: 'Deling, konto og innstillinger',
};

/**
 * Kapitlene, i den rekkefølgen de leses.
 *
 * Verktøyene har samme slug som adressen sin i appen (`/app/gjester` →
 * `gjester`), så et kapittel kan slås opp fra det verktøyet man står i uten
 * en egen tabell.
 */
export const KAPITLER: readonly Kapittel[] = [
  {
    slug: 'kom-i-gang',
    tittel: 'Kom i gang',
    ikon: 'oversikt',
    gruppe: 'start',
    ingress:
      'Hva Vår Dag er, hvordan dere oppretter bryllupet, og hvordan menyene og oversikten henger sammen.',
  },
  {
    slug: 'gjester',
    tittel: 'Gjesteliste',
    ikon: 'gjester',
    gruppe: 'verktoy',
    ingress:
      'Legg inn gjestene én og én eller som en hel liste, hold par og følge riktig, og del ut svarlenkene.',
  },
  {
    slug: 'svarsiden',
    tittel: 'Svarsiden gjestene får',
    ikon: 'svar',
    gruppe: 'verktoy',
    ingress:
      'Hva gjesten ser når de åpner lenken sin, hva de svarer på, og hva dere får vite etterpå.',
  },
  {
    slug: 'kommunikasjon',
    tittel: 'Kommunikasjon',
    ikon: 'kommunikasjon',
    gruppe: 'verktoy',
    ingress:
      'Send invitasjoner, påminnelser og beskjeder til gjestene på e-post.',
  },
  {
    slug: 'budsjett',
    tittel: 'Budsjett',
    ikon: 'budsjett',
    gruppe: 'verktoy',
    ingress:
      'Sett rammen, legg inn én post per ting som koster, og se hva som er planlagt, brukt og betalt.',
  },
  {
    slug: 'oppgaver',
    tittel: 'Oppgaver',
    ikon: 'oppgaver',
    gruppe: 'verktoy',
    ingress:
      'Den ferdige sjekklista, egne oppgaver med ansvarlig og frist, og de tre visningene liste, tavle og kalender.',
  },
  {
    slug: 'program',
    tittel: 'Dagsprogram',
    ikon: 'program',
    gruppe: 'verktoy',
    krever: 'standard',
    ingress:
      'Hva som skjer, hvor og når – slik gjestene ser det på svarsiden sin.',
  },
  {
    slug: 'bordplassering',
    tittel: 'Bordplassering',
    ikon: 'bordplassering',
    gruppe: 'verktoy',
    krever: 'plus',
    ingress:
      'Tegn opp bordene, sett gjestene ned med dra-og-slipp eller to trykk, og ta bordkartet med som PDF.',
  },
  {
    slug: 'onskeliste',
    tittel: 'Ønskeliste',
    ikon: 'onskeliste',
    gruppe: 'verktoy',
    krever: 'standard',
    ingress:
      'Legg inn gavene dere ønsker dere, med bilde og lenke. Gjestene reserverer uten at dere får vite noe.',
  },
  {
    slug: 'leverandorer',
    tittel: 'Leverandører',
    ikon: 'leverandorer',
    gruppe: 'verktoy',
    ingress: 'Finn selskapslokalet på kart eller i liste, med bilder, pris og kontaktinformasjon.',
  },
  {
    slug: 'deling',
    tittel: 'Dele planleggingen',
    ikon: 'brukere',
    gruppe: 'konto',
    ingress:
      'Den andre i brudeparet, toastmaster og forlover: hvordan de kommer inn, og hva dere lar dem se.',
  },
  {
    slug: 'konto',
    tittel: 'Konto og innlogging',
    ikon: 'innstillinger',
    gruppe: 'konto',
    ingress:
      'Passord, detaljene om bryllupet, tilbakemelding til oss, og hvordan dere sletter kontoen.',
  },
  {
    slug: 'abonnement',
    tittel: 'Abonnement og betaling',
    ikon: 'abonnement',
    gruppe: 'konto',
    ingress:
      'Pakkene Gratis, Standard og Plus, hvordan dere oppgraderer, kvitteringer og angrerett.',
  },
  {
    slug: 'personvern',
    tittel: 'Personvern og dine data',
    ikon: 'personvern',
    gruppe: 'konto',
    ingress:
      'Hva som lagres, hvor lenge, hva dere kan skru av, og hvordan dere laster ned eller sletter alt.',
  },
];

/** Kapittelet `/app/hjelp` åpner på, og `/hjelp` sender videre til. */
export const STARTKAPITTEL = 'kom-i-gang';

/**
 * Hvor hjelpesidene står.
 *
 * Den samme teksten finnes to steder: inne i appen, med sidemenyen rundt
 * (`/app/hjelp`, `/app/sporsmal`), og åpent for alle (`/hjelp`, `/sporsmal`).
 * Den åpne utgaven er til for den som ennå ikke har konto – og for
 * søkemotorene, som ikke kommer forbi innloggingen. Lenkene mellom sidene må
 * holde seg innenfor den rammen leseren alt står i; en anonym leser som
 * trykker «neste kapittel» og møter et innloggingsskjema, kommer ikke tilbake.
 */
export type Hjelperamme = 'app' | 'offentlig';

/** Adressen til de vanlige spørsmålene, inne i appen. */
export const SPORSMAALSSTI = '/app/sporsmal';

export function sporsmaalssti(ramme: Hjelperamme = 'app'): string {
  return ramme === 'app' ? SPORSMAALSSTI : '/sporsmal';
}

export function veiledningssti(slug: string, ramme: Hjelperamme = 'app'): string {
  return ramme === 'app' ? `/app/hjelp/${slug}` : `/hjelp/${slug}`;
}

export function finnKapittel(slug: string | null | undefined): Kapittel | null {
  if (!slug) return null;
  return KAPITLER.find((kapittel) => kapittel.slug === slug) ?? null;
}

/** Kapitlene før og etter, til «Forrige» og «Neste» nederst på sida. */
export function naboer(slug: string): { forrige: Kapittel | null; neste: Kapittel | null } {
  const index = KAPITLER.findIndex((kapittel) => kapittel.slug === slug);
  if (index < 0) return { forrige: null, neste: null };
  return {
    forrige: KAPITLER[index - 1] ?? null,
    neste: KAPITLER[index + 1] ?? null,
  };
}

/** Kapitlene gruppert, i rekkefølgen menyen viser dem. */
export function kapitlerEtterGruppe(): { gruppe: Kapittelgruppe; kapitler: Kapittel[] }[] {
  const rekkefolge: Kapittelgruppe[] = ['start', 'verktoy', 'konto'];
  return rekkefolge.map((gruppe) => ({
    gruppe,
    kapitler: KAPITLER.filter((kapittel) => kapittel.gruppe === gruppe),
  }));
}

/**
 * Kapittelet hjelpeboksen i et verktøy skal lenke til.
 *
 * Nøkkelen er navnet verktøyet gir `useHjelp`. Ikke alle verktøy har en slik
 * boks, og ikke alle bokser trenger et kapittel – derfor et oppslag og ikke en
 * antakelse om at navnene er like.
 */
export const KAPITTEL_FOR_HJELP: Record<string, string> = {
  gjester: 'gjester',
  budsjett: 'budsjett',
  oppgaver: 'oppgaver',
  program: 'program',
  onskeliste: 'onskeliste',
  bordplassering: 'bordplassering',
};

/* --- Vanlige spørsmål ----------------------------------------------------- */

export type Emne =
  | 'konto'
  | 'pakker'
  | 'gjester'
  | 'kommunikasjon'
  | 'planlegging'
  | 'bordplassering'
  | 'gaver'
  | 'deling'
  | 'personvern'
  | 'problemer';

/** Emnene i den rekkefølgen de vises. */
export const EMNER: readonly Emne[] = [
  'konto',
  'pakker',
  'gjester',
  'kommunikasjon',
  'planlegging',
  'bordplassering',
  'gaver',
  'deling',
  'personvern',
  'problemer',
];

export const EMNENAVN: Record<Emne, string> = {
  konto: 'Konto og innlogging',
  pakker: 'Pakker og betaling',
  gjester: 'Gjesteliste og svar',
  kommunikasjon: 'E-post til gjestene',
  planlegging: 'Budsjett, oppgaver og dagsprogram',
  bordplassering: 'Bordplassering',
  gaver: 'Ønskeliste og leverandører',
  deling: 'Toastmaster, forlover og den andre i brudeparet',
  personvern: 'Personvern og sikkerhet',
  problemer: 'Når noe ikke virker',
};

export type Sporsmal = {
  /** Står i adressen som anker: `/app/sporsmal#<id>`. */
  id: string;
  emne: Emne;
  sporsmal: string;
  /** Ett avsnitt per element. Ren tekst, så det kan søkes i. */
  svar: string[];
  /** Kapittelet i veiledningen som sier mer. */
  kapittel?: string;
  /** Andre steder i appen svaret peker til. */
  lenker?: { tekst: string; to: string }[];
  /** Ord folk søker på som ikke står i teksten: «RSVP» for «svarlenke». */
  sokeord?: string[];
};

/**
 * Gjør en tekst søkbar.
 *
 * Små bokstaver, og de norske bokstavene foldet til a, o og ae: den som
 * skriver «onskeliste» på et tastatur uten ø, skal finne ønskelista. Foldingen
 * gjøres på begge sider, så «ønskeliste» finner den også.
 */
export function normaliser(tekst: string): string {
  return tekst
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Spørsmålene som passer til et søk.
 *
 * Hvert ord i søket må finnes et sted i spørsmålet, svaret eller søkeordene.
 * «bord låst» skal finne spørsmålet om låsen på bordene, ikke alle som nevner
 * bord. Et tomt søk gir alle, i den rekkefølgen de er skrevet.
 */
export function sokISporsmal<T extends Pick<Sporsmal, 'sporsmal' | 'svar' | 'sokeord'>>(
  alle: readonly T[],
  term: string,
): T[] {
  const ord = normaliser(term).split(' ').filter(Boolean);
  if (ord.length === 0) return [...alle];
  return alle.filter((rad) => {
    const tekst = normaliser(
      [rad.sporsmal, ...rad.svar, ...(rad.sokeord ?? [])].join(' '),
    );
    return ord.every((o) => tekst.includes(o));
  });
}
